import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import styles from "./AppointmentSuccessNew.module.scss";
import {
  reinitialize,
  selectCalendarLinks,
  selectCreatedAppointment,
  selectPatient,
} from "features/scheduling/appointmentCreationSlice";
import { selectInsuranceData } from "features/insuranceDataSlice";
import { CalendarLinkModal } from "components/CalendarLinkModal";
import { parseStartTimeWithZone } from "features/appointmentTime";
import { Doctor } from "types/doctor";
import { DoctorInfoCard } from "components/DoctorInfoCard";
import { OfficeInfoCard } from "components/OfficeInfoCard";
import { Layout } from "features/layout";
import { Button, ButtonRole } from "components/Button";
import { usePageViewTracking } from "hooks/usePageViewTracking";
import { Office } from "types/office";
import { SelectedAppointment } from "types/selectedAppointment";
import { Navbar } from "components/Navbar";
import { CheckInCardNew } from "features/scheduling/components/CheckInCardNew";
import { createAboutYouNewRequest } from "api/createAboutYouRequest";
import { createInsuranceCoverageNewRequest } from "api/createInsuranceCoverageRequest";
import { skipInsuranceCoverageNewRequest } from "api/skipInsuranceCoverageRequest";
import { WithOffice } from "components/WithOffice/WithOffice";
import { NeedsSelectedAppointment } from "components/NeedsSelectedAppointment";
import { ConfirmAboutYou1 } from "features/checkIn/ConfirmAboutYou1";
import { AboutYou1FormData } from "features/checkIn/types";
import { ConfirmInsurance } from "features/checkIn/ConfirmInsurance";
import {
  canSubmitAppointmentSuccess,
  hasValidInsuranceForm,
} from "features/scheduling/appointmentSuccessValidation";
import { usePostBookingNavigation } from "features/scheduling/hooks/usePostBookingNavigation";

interface AppointmentSuccessProps {
  office: Office;
  doctor: Doctor;
  selectedAppointment: SelectedAppointment;
}

export const UnconnectedAppointmentSuccessNew: React.FC<
  AppointmentSuccessProps
> = ({ office, doctor, selectedAppointment }) => {
  const parsedTime = parseStartTimeWithZone(selectedAppointment);
  const formattedDate = parsedTime.format("ddd. MMM D, YYYY");
  const formattedTime = parsedTime.format("h:mm A");
  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isSubmiting, setSubmiting] = useState(false);
  const [addressDATA, setAddressDATA] = useState<AboutYou1FormData>();
  const [insChecked, setInsChecked] = useState(false);
  const createdAppointment = useSelector(selectCreatedAppointment);
  const { continueToCheckIn } = usePostBookingNavigation(createdAppointment?.id);
  const patient = useSelector(selectPatient);
  const insuranceFormDataArr = useSelector(selectInsuranceData);
  const canTrackSuccessEvent =
    !!createdAppointment?.patientId && !!createdAppointment?.id;
  usePageViewTracking("step-8-appointment-created", {
    doctorId: doctor.id,
    patientId: createdAppointment?.patientId,
    patientPhoneNumber: patient?.phoneNumber,
    patientSmsOptIn: patient?.smsOptIn,
    appointmentId: createdAppointment?.id,
    appointmentDate: parsedTime.format("YYYY-MM-DD"),
    appointmentTime: formattedTime,
  }, {
    shouldTrack: canTrackSuccessEvent,
    dependencies: [createdAppointment?.patientId, createdAppointment?.id],
  });
  const calendarLinks = useSelector(selectCalendarLinks);

  useEffect(() => {
    return () => {
      dispatch(reinitialize());
    };
  }, [dispatch]);

  const onSubmitNew = async () => {
    setSubmiting(true);
    let isFormValid = false;
    const dummyArr = [];
    if (
      !isEmpty(insuranceFormDataArr[0]) &&
      hasValidInsuranceForm(insuranceFormDataArr[0])
    ) {
      dummyArr.push(insuranceFormDataArr[0]);
    }
    if (
      !isEmpty(insuranceFormDataArr[1]) &&
      hasValidInsuranceForm(insuranceFormDataArr[1])
    ) {
      dummyArr.push(insuranceFormDataArr[1]);
    }
    for (const insurance of insuranceFormDataArr) {
      if (!isEmpty(insurance)) {
        isFormValid = hasValidInsuranceForm(insurance) ? true : false;
        if (!isFormValid) break;
      }
    }
    if (
      canSubmitAppointmentSuccess({
        aboutYouData: addressDATA,
        insuranceForms: insuranceFormDataArr,
        skipInsurance: insChecked,
      })
    ) {
      await createAboutYouNewRequest({
        ...addressDATA,
      });
      if (!insChecked) {
        await createInsuranceCoverageNewRequest(dummyArr);
      } else {
        await skipInsuranceCoverageNewRequest();
      }
      setSubmiting(false);
      await continueToCheckIn();
    } else {
      setTimeout(() => {
        setSubmiting(false);
      }, 2000);
    }
  };

  const getAddressValues = (addressData: any) => {
    setAddressDATA(addressData);
  };

  const getInsuranceValues = async (_insuranceData: any) => {
    setSubmiting(false);
  };

  const handleChangeIns = () => {
    setInsChecked(!insChecked);
  };

  const isSubmitDisabled = !canSubmitAppointmentSuccess({
    aboutYouData: addressDATA,
    insuranceForms: insuranceFormDataArr,
    skipInsurance: insChecked,
  });

  return (
    <Layout navbar={<Navbar office={office} />}>
      <div data-testid="appElement" className={styles.container}>
        <div className={styles.twoColumns}>
          <div className={styles.column}>
            <p className={styles.youreScheduled}>You’re Scheduled!</p>
            <h2 className={styles.subheader}>
              {formattedDate} at {formattedTime}
            </h2>
            <div>
              <OfficeInfoCard office={office} />
              <DoctorInfoCard doctor={doctor} office={office} />
            </div>
            <Button
              className={styles.calendarButton}
              text="Add to Calendar"
              onClick={(ev) => {
                setModalOpen(true);
                ev.stopPropagation();
              }}
              role={ButtonRole.Ghost}
              size="large"
            />
            <ConfirmAboutYou1
              aboutYou1Data={null}
              getAddressValues={getAddressValues}
              isSubmit={isSubmiting}
              disableProgressGuard={true}
            />
            <div className={styles.insCheck}>
              <input
                id="success-new-skip-insurance"
                type="checkbox"
                checked={insChecked}
                onChange={handleChangeIns}
              />
              <label htmlFor="success-new-skip-insurance">
                {" "}
                I WILL NOT BE USING INSURANCE
              </label>
            </div>
            {!insChecked && (
              <ConfirmInsurance
                defaultValues={null}
                isSubmit={isSubmiting}
                getInsuranceValues={getInsuranceValues}
                currentPatient={patient}
                disableProgressGuard={true}
              />
            )}
            <CheckInCardNew
              appointment={selectedAppointment}
              onClick={onSubmitNew}
              isSubmit={isSubmiting}
              isDisabled={isSubmitDisabled}
            />
          </div>
        </div>
      </div>
      <CalendarLinkModal
        onRequestClose={() => setModalOpen(false)}
        isModalOpen={isModalOpen}
        links={calendarLinks}
      />
    </Layout>
  );
};

export const AppointmentSuccessNew: React.FC = () => (
  <WithOffice>
    {({ office }) => (
      <NeedsSelectedAppointment>
        {({ selectedAppointment, doctor }) => (
          <UnconnectedAppointmentSuccessNew
            office={office}
            doctor={doctor}
            selectedAppointment={selectedAppointment}
          />
        )}
      </NeedsSelectedAppointment>
    )}
  </WithOffice>
);
